import { useCallback, useEffect, useRef, useState } from "react";
import { CircleX, Library, RotateCcw, X } from "lucide-react";
import ToggleSwitch from "./ToggleSwitch";
import GroupementAssignDropdown from "./GroupementAssignDropdown";
import { fetchGroupements } from "../utils/groupementsApi";
import { CAPTURE_AFTER_BEHAVIORS } from "../constants/capturePreferences";
import { MENU_CAPTURE_TOOLTIPS } from "../constants/menuTooltips";
import MenuIconTooltip from "./MenuIconTooltip";
import VocabInfoTip from "./VocabInfoTip";
import { reportQuestCapture } from "../utils/questProgressReporter";
import { pickTitleWordCounts } from "../utils/titleWordCounts";
import { API_URL } from "../config/api";
import { dispatchNotificationsChanged } from "../utils/levelHistoryApi";
import { fetchWikipediaDefinition } from "../services/wikipedia";
import { fetchWiktionaryDefinition } from "../services/wiktionary";
import {
  ILLUSTRATION_ACCEPT,
  prepareWordIllustration,
  uploadWordIllustration,
} from "../utils/wordIllustration";
import {
  prepareWordFicheWrite,
  isBaseMotCache,
  motCacheAlreadyUsed,
  sanitizeMotKey,
  syncBaseMotCache,
  sanitizeWordLabel,
  sanitizeWordRichText,
  filterTypedWord,
  validateWordFicheFields,
  validateWordMot,
  WORD_MOT_MAX_LENGTH,
  WORD_LIVRE_MAX_LENGTH,
  WORD_AUTEUR_MAX_LENGTH,
  WORD_RICH_TEXT_MAX_LENGTH,
  WORD_CACHE_MAX_LENGTH,
  WORD_CACHES_MAX,
} from "../utils/wordSanitize";
import DictionarySourcePanel from "./DictionarySourcePanel";
import WordFieldCharCount from "./WordFieldCharCount";
import { findCloseRegisterWords } from "../utils/frenchStem";
import CaptureFeedbackModal from "./CaptureFeedbackModal";
import CaptureSourcePickerModal from "./CaptureSourcePickerModal";
import "./CaptureDeMot.css";
import "./WordEditPanel.css";

const CAPTURE_SOURCE_KEY = "vocabularium_capture_last_source";

function readCaptureSource() {
  try {
    const raw = localStorage.getItem(CAPTURE_SOURCE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const auteur = String(parsed?.auteur || "").trim();
    const livre = String(parsed?.livre || "").trim();
    if (!auteur || !livre) return null;
    return { auteur, livre };
  } catch {
    return null;
  }
}

function saveCaptureSource(auteur, livre) {
  const trimmedAuteur = auteur.trim();
  const trimmedLivre = livre.trim();
  if (!trimmedAuteur || !trimmedLivre) return;

  localStorage.setItem(
    CAPTURE_SOURCE_KEY,
    JSON.stringify({ auteur: trimmedAuteur, livre: trimmedLivre })
  );
}

function UnderlineInput({
  value,
  onChange,
  placeholder,
  className = "",
  ariaLabel,
  centered = false,
  maxLength,
  expandFromChar,
  expandUntilChar,
  expandBaseWidth = "220px",
}) {
  const [focused, setFocused] = useState(false);
  const showGhost = !focused && !value.trim();
  const expandAfter = Number(expandFromChar);
  const expandUntil = Number(expandUntilChar);
  const growCap =
    Number.isFinite(expandUntil) && expandUntil >= expandAfter ? expandUntil : Infinity;
  const extraChars =
    Number.isFinite(expandAfter) && expandAfter > 0 && value.length >= expandAfter
      ? Math.min(value.length, growCap) - (expandAfter - 1)
      : 0;
  const expandStyle =
    extraChars > 0
      ? {
          width: `calc(${expandBaseWidth} + ${extraChars}ch)`,
          maxWidth: Number.isFinite(growCap)
            ? `calc(${expandBaseWidth} + ${growCap - expandAfter + 1}ch)`
            : undefined,
        }
      : undefined;

  return (
    <span
      className={`capture-de-mot__field-wrap${centered ? " capture-de-mot__field-wrap--centered" : ""} ${className}`.trim()}
      style={expandStyle}
    >
      {showGhost && (
        <span className="capture-de-mot__ghost" aria-hidden="true">
          {placeholder}
        </span>
      )}
      <input
        type="text"
        className="capture-de-mot__inline-input"
        value={value}
        maxLength={maxLength}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        aria-label={ariaLabel}
      />
    </span>
  );
}

const DICT_RESULT_INITIAL = {
  status: "idle",
  title: "",
  text: "",
  url: "",
};

function applyDictionaryResult(setter, query, result, sourceName) {
  if (result.ok) {
    setter({
      status: "success",
      title: sanitizeWordLabel(result.title),
      text: sanitizeWordRichText(result.extract),
      url: result.url,
    });
    return;
  }

  if (
    result.reason === "not-found" ||
    result.reason === "no-french-section" ||
    result.reason === "empty-page"
  ) {
    setter({
      status: "not-found",
      title: "",
      text: `Aucune entrée ${sourceName} trouvée pour « ${result.query || query} ».`,
      url: "",
    });
    return;
  }

  setter({
    status: "error",
    title: "",
    text: `Impossible de contacter ${sourceName}. Réessayez dans un instant.`,
    url: "",
  });
}

function preparedCaptureFields(capture) {
  if (!capture?.mot) return null;
  const mot = sanitizeMotKey(capture.mot);
  if (!mot) return null;
  return {
    mot,
    auteur: sanitizeWordLabel(capture.auteur || "").slice(0, WORD_AUTEUR_MAX_LENGTH),
    livre: sanitizeWordLabel(capture.livre || "").slice(0, WORD_LIVRE_MAX_LENGTH),
    extrait: sanitizeWordRichText(capture.extrait || "").slice(0, WORD_RICH_TEXT_MAX_LENGTH),
  };
}

export default function CaptureDeMot({
  onClose,
  embedded = false,
  panel = "capture",
  preparedCapture = null,
  afterCaptureBehavior = CAPTURE_AFTER_BEHAVIORS.REGISTER,
}) {
  const openingCapture = preparedCaptureFields(preparedCapture);
  const openingReady = Boolean(openingCapture && !validateWordMot(openingCapture.mot));
  const [step, setStep] = useState(openingReady ? "form" : "check");
  const [wordConfirmed, setWordConfirmed] = useState(false);
  const [wordSealed, setWordSealed] = useState(openingReady);
  const [mot, setMot] = useState(openingCapture?.mot || "");
  const [dictQuery, setDictQuery] = useState("");
  const [wiktionaryResult, setWiktionaryResult] = useState(DICT_RESULT_INITIAL);
  const [wikipediaResult, setWikipediaResult] = useState(DICT_RESULT_INITIAL);
  const [searchingDict, setSearchingDict] = useState(false);
  const [fetchingCaptureDefinition, setFetchingCaptureDefinition] = useState(false);
  const [dictTitleFocused, setDictTitleFocused] = useState(false);
  const [formData, setFormData] = useState({
    auteur: openingCapture?.auteur || "",
    livre: openingCapture?.livre || "",
    definition: "",
    extrait: openingCapture?.extrait || "",
    date: new Date().toLocaleDateString("fr-FR"),
  });
  const [motsCaches, setMotsCaches] = useState(openingCapture?.mot ? [openingCapture.mot] : [""]);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isPriority, setIsPriority] = useState(false);
  const [groupementId, setGroupementId] = useState("");
  const [groupements, setGroupements] = useState([]);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [priorityCount, setPriorityCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [checking, setChecking] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [closeWords, setCloseWords] = useState([]);
  const [canRestoreSource, setCanRestoreSource] = useState(false);
  const [sourcePickerOpen, setSourcePickerOpen] = useState(false);
  const [titleFocused, setTitleFocused] = useState(false);
  const [illustrationDataUrl, setIllustrationDataUrl] = useState(null);
  const [illustrationBusy, setIllustrationBusy] = useState(false);

  const modalRef = useRef(null);
  const prevMotRef = useRef(openingCapture?.mot || "");
  const preparedCaptureRef = useRef(preparedCapture);
  preparedCaptureRef.current = preparedCapture;
  const wordInputRef = useRef(null);
  const illustrationInputRef = useRef(null);
  const captureGenerationRef = useRef(0);
  const registerWordsRef = useRef(null);
  const registerLoadRef = useRef(null);

  const loadRegisterWords = useCallback(() => {
    if (registerWordsRef.current) return Promise.resolve(registerWordsRef.current);
    if (!registerLoadRef.current) {
      registerLoadRef.current = fetch(`${API_URL}/mots-liste`)
        .then((res) => (res.ok ? res.json() : []))
        .then((list) => {
          const words = (Array.isArray(list) ? list : [])
            .map((entry) => sanitizeMotKey(entry))
            .filter(Boolean);
          registerWordsRef.current = words;
          return words;
        })
        .catch(() => {
          registerLoadRef.current = null;
          return [];
        });
    }
    return registerLoadRef.current;
  }, []);

  const noteCloseWords = useCallback(
    async (motFinal) => {
      try {
        const words = await loadRegisterWords();
        setCloseWords(findCloseRegisterWords(motFinal, words));
      } catch {
        setCloseWords([]);
      }
    },
    [loadRegisterWords]
  );

  useEffect(() => {
    loadRegisterWords();
  }, [loadRegisterWords]);

  useEffect(() => {
    if (embedded) return undefined;

    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose, embedded]);

  const loadQuotas = useCallback(async () => {
    try {
      const [favRes, priRes] = await Promise.all([
        fetch(`${API_URL}/favoris-liste`),
        fetch(`${API_URL}/prioritaires-liste`),
      ]);

      if (favRes.ok) {
        const list = await favRes.json();
        setFavoritesCount(Array.isArray(list) ? list.length : 0);
      }
      if (priRes.ok) {
        const list = await priRes.json();
        setPriorityCount(Array.isArray(list) ? list.length : 0);
      }
    } catch (err) {
      console.error("Erreur chargement quotas capture :", err);
    }
  }, []);

  useEffect(() => {
    loadQuotas();
  }, [loadQuotas]);

  useEffect(() => {
    let cancelled = false;
    fetchGroupements()
      .then((items) => {
        if (!cancelled) setGroupements(Array.isArray(items) ? items : []);
      })
      .catch((err) => {
        console.error("Erreur chargement groupements capture :", err);
        if (!cancelled) setGroupements([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setCanRestoreSource(Boolean(readCaptureSource()));
  }, []);

  useEffect(() => {
    const fields = preparedCaptureFields(preparedCaptureRef.current);
    if (!preparedCapture?.token || !fields) return undefined;
    let cancelled = false;
    setMot(fields.mot);
    setFormData({
      auteur: fields.auteur,
      livre: fields.livre,
      definition: "",
      extrait: fields.extrait,
      date: new Date().toLocaleDateString("fr-FR"),
    });
    setMotsCaches([fields.mot]);
    prevMotRef.current = fields.mot;
    setDuplicateInfo(null);
    setCloseWords([]);
    setWordConfirmed(false);
    if (validateWordMot(fields.mot)) {
      setStep("check");
      setWordSealed(false);
      return undefined;
    }
    setStep("form");
    setWordSealed(true);
    setChecking(true);
    fetch(`${API_URL}/mots/${encodeURIComponent(fields.mot)}`)
      .then(async (checkRes) => {
        if (cancelled) return;
        if (checkRes.ok) {
          const existing = await checkRes.json();
          setDuplicateInfo({
            mot: existing.mot || fields.mot,
            livre: existing.livre || "",
            auteur: existing.auteur || "",
            date: existing.date || "",
          });
          setWordConfirmed(false);
          return;
        }
        if (checkRes.status === 404) {
          setWordConfirmed(true);
          const words = await loadRegisterWords();
          if (!cancelled) setCloseWords(findCloseRegisterWords(fields.mot, words));
          return;
        }
        setWordSealed(false);
      })
      .catch(() => {
        if (!cancelled) setWordSealed(false);
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [preparedCapture?.token, loadRegisterWords]);

  useEffect(() => {
    if (!duplicateInfo) return undefined;

    const timer = setTimeout(() => setDuplicateInfo(null), 5000);
    return () => clearTimeout(timer);
  }, [duplicateInfo]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const filledMotsCachesCount = motsCaches.filter((entry) => entry.trim()).length;

  const updateMotCacheRow = (index, value) => {
    if (isBaseMotCache(motsCaches[index], mot)) return;
    if (motCacheAlreadyUsed(motsCaches, index, value)) return;
    setMotsCaches((prev) =>
      prev.map((entry, rowIndex) => (rowIndex === index ? value : entry))
    );
  };

  const addMotCacheRow = () => {
    if (motsCaches.length >= WORD_CACHES_MAX) return;
    if (motsCaches.some((entry) => !entry.trim())) return;
    setMotsCaches((prev) => [...prev, ""]);
  };

  const removeMotCacheRow = (index) => {
    if (motsCaches.length <= 1) return;
    if (isBaseMotCache(motsCaches[index], mot)) return;
    setMotsCaches((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
  };

  const canAddMotCacheRow =
    motsCaches.length < WORD_CACHES_MAX && motsCaches.every((entry) => entry.trim().length > 0);

  const handleFavoriteChange = (checked) => {
    setIsFavorite(checked);
  };

  const handlePriorityChange = (checked) => {
    setIsPriority(checked);
  };

  const handleRestoreSource = () => {
    const saved = readCaptureSource();
    if (!saved) return;

    setFormData((prev) => ({
      ...prev,
      auteur: saved.auteur,
      livre: saved.livre,
    }));
  };

  const handleClearSource = () => {
    setFormData((prev) => ({
      ...prev,
      auteur: "",
      livre: "",
    }));
  };

  const handlePickExistingSource = ({ auteur, livre }) => {
    setFormData((prev) => ({
      ...prev,
      auteur: auteur || "",
      livre: livre || "",
    }));
  };

  const handleWordInputFocus = () => {
    setTitleFocused(true);
    setDuplicateInfo(null);
    setCloseWords([]);
  };

  const handleVerify = async () => {
    const motFinal = sanitizeMotKey(mot);
    if (validateWordMot(motFinal) || checking) return;

    setChecking(true);
    setDuplicateInfo(null);
    setCloseWords([]);

    try {
      const checkRes = await fetch(`${API_URL}/mots/${encodeURIComponent(motFinal)}`);

      if (checkRes.ok) {
        const existing = await checkRes.json();
        setDuplicateInfo({
          mot: existing.mot || motFinal,
          livre: existing.livre || "",
          auteur: existing.auteur || "",
          date: existing.date || "",
        });
        return;
      }

      if (checkRes.status === 404) {
        const previousMot = prevMotRef.current;
        setMot(motFinal);
        if (step === "form") {
          setMotsCaches((prev) => syncBaseMotCache(prev, motFinal, previousMot));
        } else {
          setMotsCaches([motFinal]);
          setStep("form");
        }
        prevMotRef.current = motFinal;
        setWordConfirmed(true);
        setWordSealed(true);
        await noteCloseWords(motFinal);
        return;
      }

      alert("Impossible de vérifier si le mot existe déjà.");
    } catch (err) {
      console.error("Erreur vérification mot :", err);
      alert("Erreur de connexion au serveur.");
    } finally {
      setChecking(false);
    }
  };

  const handleWordKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleVerify();
    }
  };

  const handleDictSearch = async () => {
    const query = dictQuery.trim();
    if (query.length < 2 || searchingDict) return;

    setSearchingDict(true);
    setWiktionaryResult({ ...DICT_RESULT_INITIAL, status: "loading" });
    setWikipediaResult({ ...DICT_RESULT_INITIAL, status: "loading" });

    try {
      const [wiktionaryLookup, wikipediaLookup] = await Promise.all([
        fetchWiktionaryDefinition(query),
        fetchWikipediaDefinition(query),
      ]);

      applyDictionaryResult(setWiktionaryResult, query, wiktionaryLookup, "Wiktionary");
      applyDictionaryResult(setWikipediaResult, query, wikipediaLookup, "Wikipédia");
    } catch (err) {
      console.error("Erreur recherche dictionnaire :", err);
      setWiktionaryResult({
        status: "error",
        title: "",
        text: "Impossible de contacter Wiktionary. Réessayez dans un instant.",
        url: "",
      });
      setWikipediaResult({
        status: "error",
        title: "",
        text: "Impossible de contacter Wikipédia. Réessayez dans un instant.",
        url: "",
      });
    } finally {
      setSearchingDict(false);
    }
  };

  const handleDictKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleDictSearch();
    }
  };

  const handleFetchCaptureDefinition = async () => {
    const word = mot.trim();
    if (!word || fetchingCaptureDefinition) return;

    const generation = captureGenerationRef.current;
    setFetchingCaptureDefinition(true);
    try {
      const result = await fetchWiktionaryDefinition(word);
      if (generation !== captureGenerationRef.current) return;

      if (result.ok) {
        handleChange("definition", sanitizeWordRichText(result.extract));
        return;
      }

      if (
        result.reason === "not-found" ||
        result.reason === "no-french-section" ||
        result.reason === "empty-page"
      ) {
        alert(`Aucune entrée Wiktionary trouvée pour « ${result.query || word} ».`);
        return;
      }

      alert("Impossible de contacter Wiktionary. Réessayez dans un instant.");
    } catch (err) {
      console.error("Erreur Wiktionary capture :", err);
      if (generation !== captureGenerationRef.current) return;
      alert("Impossible de contacter Wiktionary. Réessayez dans un instant.");
    } finally {
      if (generation === captureGenerationRef.current) {
        setFetchingCaptureDefinition(false);
      }
    }
  };

  const handleClearDefinition = () => {
    handleChange("definition", "");
  };

  const handleClearExtrait = () => {
    handleChange("extrait", "");
  };

  const handleAddIllustrationClick = () => {
    if (illustrationBusy || illustrationDataUrl) return;
    illustrationInputRef.current?.click();
  };

  const handleIllustrationSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const generation = captureGenerationRef.current;
    setIllustrationBusy(true);
    try {
      const dataUrl = await prepareWordIllustration(file);
      if (generation !== captureGenerationRef.current) return;
      setIllustrationDataUrl(dataUrl);
    } catch (err) {
      console.error("Erreur illustration capture :", err);
      if (generation !== captureGenerationRef.current) return;
      alert(err.message || "Impossible d'ajouter l'illustration.");
    } finally {
      if (generation === captureGenerationRef.current) {
        setIllustrationBusy(false);
      }
    }
  };

  const handleClearIllustration = () => {
    if (illustrationBusy) return;
    setIllustrationDataUrl(null);
  };

  const dictQueryCoreLen = dictQuery.trim().replace(/\s+/g, "").length;
  const canSearchDict = dictQueryCoreLen >= 2 && !searchingDict;
  const showDictionary = panel === "dictionary";
  const showCaptureCheck = !showDictionary && step === "check";
  const showCaptureForm = !showDictionary && step === "form";
  const wordLocked = wordConfirmed || wordSealed;
  const wordIsEditable = showCaptureCheck || (showCaptureForm && !wordLocked);

  useEffect(() => {
    if (wordIsEditable) {
      wordInputRef.current?.focus();
    }
  }, [wordIsEditable]);

  const resetCaptureFields = () => {
    captureGenerationRef.current += 1;
    setStep("check");
    setWordConfirmed(false);
    setWordSealed(false);
    setMot("");
    prevMotRef.current = "";
    setMotsCaches([""]);
    setIsFavorite(false);
    setIsPriority(false);
    setGroupementId("");
    setDuplicateInfo(null);
    setCloseWords([]);
    setFormData({
      auteur: "",
      livre: "",
      definition: "",
      extrait: "",
      date: new Date().toLocaleDateString("fr-FR"),
    });
    setIllustrationDataUrl(null);
    setIllustrationBusy(false);
    setFetchingCaptureDefinition(false);
    loadQuotas();
  };

  const resetForNewCapture = () => {
    resetCaptureFields();
  };

  const handleRestartWord = () => {
    setWordConfirmed(false);
    setWordSealed(false);
    setDuplicateInfo(null);
    setCloseWords([]);
    setTitleFocused(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step !== "form" || !wordConfirmed || !isFormValid || submitting) return;

    const fiche = prepareWordFicheWrite(
      { ...formData, mot, motsCaches },
      { includeMot: true }
    );

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/mots`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mot: fiche.mot,
          auteur: fiche.auteur,
          livre: fiche.livre,
          definition: fiche.definition,
          extrait: fiche.extrait,
          date: formData.date,
          wildScore: 0,
          motsCaches: fiche.motsCaches,
          categorie: {
            coeur: isFavorite ? 1 : 0,
            sword: 0,
            fail: 0,
            top20: isPriority ? 1 : 0,
          },
          groupementId: groupementId || null,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setFeedback({
          variant: "error",
          message: data.message || "Erreur lors de la capture du mot.",
        });
        return;
      }

      if (illustrationDataUrl) {
        try {
          await uploadWordIllustration(fiche.mot, illustrationDataUrl);
        } catch (err) {
          console.error("Erreur enregistrement illustration capture :", err);
        }
      }

      saveCaptureSource(formData.auteur, formData.livre);
      setCanRestoreSource(true);
      if (fiche.mot) {
        const known = registerWordsRef.current || [];
        if (!known.includes(fiche.mot)) {
          registerWordsRef.current = [...known, fiche.mot];
        }
      }
      setFeedback({ variant: "success" });
      reportQuestCapture(1, pickTitleWordCounts(data));
    } catch (err) {
      console.error("Erreur réseau capture :", err);
      setFeedback({
        variant: "error",
        message: "Erreur de connexion au serveur.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const canVerify = !validateWordMot(mot) && !checking;

  const isFormValid = !validateWordFicheFields({
    mot,
    auteur: formData.auteur,
    livre: formData.livre,
    definition: formData.definition,
    extrait: formData.extrait,
    motsCaches,
    includeMot: true,
  });

  const handleFeedbackClose = () => {
    const wasSuccess = feedback?.variant === "success";
    setFeedback(null);
    if (wasSuccess) {
      dispatchNotificationsChanged();
      if (afterCaptureBehavior === CAPTURE_AFTER_BEHAVIORS.STAY) {
        resetForNewCapture();
      } else {
        onClose();
      }
    }
  };

  return (
    <div className={`capture-de-mot${embedded ? " capture-de-mot--embedded" : ""}`}>
      <CaptureFeedbackModal
        open={Boolean(feedback)}
        variant={feedback?.variant || "success"}
        message={feedback?.message}
        onClose={handleFeedbackClose}
      />
      <CaptureSourcePickerModal
        open={sourcePickerOpen}
        currentLivre={formData.livre}
        currentAuteur={formData.auteur}
        onClose={() => setSourcePickerOpen(false)}
        onSelect={handlePickExistingSource}
      />
      <div
        ref={modalRef}
        className={`capture-de-mot__panel${embedded ? " capture-de-mot__panel--embedded" : ""}`}
      >
        {!embedded && (
          <button
            type="button"
            className="capture-de-mot__close"
            onClick={onClose}
            aria-label="Fermer"
          >
            ×
          </button>
        )}

        <form onSubmit={handleSubmit} className="capture-de-mot__form">
          <header className="capture-de-mot__header">
            {showDictionary ? (
              <h2 className="capture-de-mot__title capture-de-mot__title--with-action">
                <span className="capture-de-mot__title-label">Dictionnaire :</span>{" "}
                <span className="capture-de-mot__field-wrap capture-de-mot__field-wrap--title capture-de-mot__field-wrap--centered">
                  {!dictTitleFocused && !dictQuery.trim() && (
                    <span
                      className="capture-de-mot__ghost capture-de-mot__ghost--title"
                      aria-hidden="true"
                    >
                      votre mot
                    </span>
                  )}
                  <input
                    type="text"
                    className="capture-de-mot__title-input"
                    value={dictQuery}
                    onChange={(e) => setDictQuery(e.target.value)}
                    onFocus={() => setDictTitleFocused(true)}
                    onBlur={() => setDictTitleFocused(false)}
                    onKeyDown={handleDictKeyDown}
                    aria-label="Mot à rechercher"
                  />
                </span>
                <button
                  type="button"
                  className="word-edit-btn word-edit-btn--primary capture-de-mot__verify-btn"
                  onClick={handleDictSearch}
                  disabled={!canSearchDict}
                >
                  {searchingDict ? "Recherche…" : "Rechercher"}
                </button>
              </h2>
            ) : (
              <h2
                className={`capture-de-mot__title${
                  wordIsEditable || showCaptureForm ? " capture-de-mot__title--with-action" : ""
                }${showCaptureForm && wordLocked ? " capture-de-mot__title--word-reset" : ""}`}
              >
                <span className="capture-de-mot__title-label">Capture de mot :</span>{" "}
                {wordIsEditable ? (
                  <>
                    <span className="capture-de-mot__field-wrap capture-de-mot__field-wrap--title capture-de-mot__field-wrap--centered">
                      {!titleFocused && !mot.trim() && (
                        <span
                          className="capture-de-mot__ghost capture-de-mot__ghost--title"
                          aria-hidden="true"
                        >
                          votre mot
                        </span>
                      )}
                      <input
                        ref={wordInputRef}
                        type="text"
                        className="capture-de-mot__title-input"
                        value={mot}
                        onChange={(e) => {
                          setMot(filterTypedWord(e.target.value));
                          setDuplicateInfo(null);
                          setCloseWords([]);
                        }}
                        maxLength={WORD_MOT_MAX_LENGTH}
                        onFocus={handleWordInputFocus}
                        onBlur={() => setTitleFocused(false)}
                        onKeyDown={handleWordKeyDown}
                        aria-label="Mot à capturer"
                      />
                    </span>
                    <button
                      type="button"
                      className="word-edit-btn word-edit-btn--primary capture-de-mot__verify-btn"
                      onClick={handleVerify}
                      disabled={!canVerify}
                    >
                      {checking ? "Vérification…" : "Vérifier"}
                    </button>
                  </>
                ) : (
                  <span className="capture-de-mot__title-word-wrap">
                    <span className="capture-de-mot__title-word">{mot}</span>
                    <MenuIconTooltip
                      tooltip={MENU_CAPTURE_TOOLTIPS.restartWord}
                      placement="above"
                      portal
                    >
                      <button
                        type="button"
                        className="capture-de-mot__source-btn"
                        onClick={handleRestartWord}
                        aria-label={MENU_CAPTURE_TOOLTIPS.restartWord}
                      >
                        <CircleX size={16} strokeWidth={1.8} aria-hidden="true" />
                      </button>
                    </MenuIconTooltip>
                  </span>
                )}
              </h2>
            )}

            {!showDictionary && duplicateInfo && (
              <p className="capture-de-mot__duplicate-msg" role="alert">
                Le mot <strong>{duplicateInfo.mot}</strong> a déjà été capturé dans{" "}
                <strong>{duplicateInfo.livre}</strong> de{" "}
                <strong>{duplicateInfo.auteur}</strong> le{" "}
                <strong>{duplicateInfo.date}</strong>.
              </p>
            )}

            {!showDictionary && !duplicateInfo && closeWords.length > 0 && (
              <p className="capture-de-mot__close-msg" role="status">
                Attention,{" "}
                {closeWords.length > 1
                  ? "des mots proches existent déjà dans le registre : "
                  : "un mot proche existe déjà dans le registre : "}
                {closeWords.map((closeWord, index) => (
                  <span key={closeWord}>
                    {index > 0 ? ", " : ""}
                    <strong>{closeWord}</strong>
                  </span>
                ))}
                .
              </p>
            )}

            {showCaptureForm && (
              <p className="capture-de-mot__meta">
                Capturé le <strong>{formData.date}</strong>, dans{" "}
                <UnderlineInput
                  className="capture-de-mot__inline-input--livre"
                  value={formData.livre}
                  onChange={(e) =>
                    handleChange("livre", e.target.value.slice(0, WORD_LIVRE_MAX_LENGTH))
                  }
                  placeholder="nom du livre"
                  ariaLabel="Livre"
                  maxLength={WORD_LIVRE_MAX_LENGTH}
                  expandFromChar={21}
                  expandUntilChar={45}
                />
                , par{" "}
                <UnderlineInput
                  className="capture-de-mot__inline-input--auteur"
                  value={formData.auteur}
                  onChange={(e) =>
                    handleChange("auteur", e.target.value.slice(0, WORD_AUTEUR_MAX_LENGTH))
                  }
                  placeholder="nom de l'auteur"
                  ariaLabel="Auteur"
                  maxLength={WORD_AUTEUR_MAX_LENGTH}
                  expandFromChar={21}
                  expandUntilChar={45}
                  expandBaseWidth="200px"
                />
                <span className="capture-de-mot__source-actions">
                  <MenuIconTooltip
                    tooltip={MENU_CAPTURE_TOOLTIPS.restoreSource}
                    placement="above"
                    portal
                  >
                    <button
                      type="button"
                      className="capture-de-mot__source-btn"
                      onClick={handleRestoreSource}
                      disabled={!canRestoreSource}
                      aria-label={MENU_CAPTURE_TOOLTIPS.restoreSource}
                    >
                      <RotateCcw size={16} strokeWidth={1.8} />
                    </button>
                  </MenuIconTooltip>
                  <MenuIconTooltip
                    tooltip={MENU_CAPTURE_TOOLTIPS.pickSource}
                    placement="above"
                    portal
                  >
                    <button
                      type="button"
                      className="capture-de-mot__source-btn"
                      onClick={() => setSourcePickerOpen(true)}
                      aria-label={MENU_CAPTURE_TOOLTIPS.pickSource}
                    >
                      <Library size={16} strokeWidth={1.8} />
                    </button>
                  </MenuIconTooltip>
                  <MenuIconTooltip
                    tooltip={MENU_CAPTURE_TOOLTIPS.clearSource}
                    placement="above"
                    portal
                  >
                    <button
                      type="button"
                      className="capture-de-mot__source-btn"
                      onClick={handleClearSource}
                      disabled={!formData.livre.trim() && !formData.auteur.trim()}
                      aria-label={MENU_CAPTURE_TOOLTIPS.clearSource}
                    >
                      <CircleX size={16} strokeWidth={1.8} />
                    </button>
                  </MenuIconTooltip>
                </span>
              </p>
            )}
          </header>

          {showDictionary && (
            <section className="capture-de-mot__dictionary" aria-live="polite">
              <DictionarySourcePanel
                label="Wiktionary"
                siteName="Wiktionary"
                result={wiktionaryResult}
                infoTooltip={MENU_CAPTURE_TOOLTIPS.wiktionaryIndicative}
                infoAriaLabel="À propos de Wiktionary"
              />
              <DictionarySourcePanel
                label="Wikipédia"
                siteName="Wikipédia"
                result={wikipediaResult}
                infoTooltip={MENU_CAPTURE_TOOLTIPS.wikipediaIndicative}
                infoAriaLabel="À propos de Wikipédia"
              />
            </section>
          )}

          {showCaptureForm && (
            <>
              <div className="capture-de-mot__columns">
                  <div className="capture-de-mot__definition-head">
                    <label className="capture-de-mot__label" htmlFor="capture-definition">
                      <span className="capture-de-mot__label-title">
                        Définition
                        <VocabInfoTip
                          className="capture-de-mot__definition-info"
                          tooltip={
                            <>
                              <span className="vocab-tooltip-line" style={{ display: "block" }}>
                                CONSEIL : privilégiez le Larousse, le Robert, le dictionnaire de l'Académie française et le CNRTL.
                              </span>
                              <span
                                className="vocab-tooltip-line"
                                style={{ display: "block", marginTop: 4 }}
                              >
                                Vous pouvez utiliser Wikipédia en dernier recours (avec parcimonie et de manière éclairée) pour personnaliser ou simplifier une définition trop complexe / technique.
                              </span>
                            </>
                          }
                          ariaLabel="Conseil pour rédiger la définition"
                        />
                      </span>
                      <WordFieldCharCount
                        value={formData.definition}
                        maxLength={WORD_RICH_TEXT_MAX_LENGTH}
                      />
                    </label>
                    <button
                      type="button"
                      className="capture-de-mot__wiktionary-btn"
                      onClick={handleFetchCaptureDefinition}
                      disabled={!mot.trim() || fetchingCaptureDefinition}
                    >
                      {fetchingCaptureDefinition
                        ? "Recherche…"
                        : "Rechercher une définition"}
                    </button>
                  </div>
                  <div className="capture-de-mot__textarea-wrap capture-de-mot__textarea-wrap--definition">
                    <textarea
                      id="capture-definition"
                      className="capture-de-mot__textarea"
                      value={formData.definition}
                      onChange={(e) => handleChange("definition", e.target.value)}
                    />
                    <MenuIconTooltip
                      className="capture-de-mot__textarea-clear-tip"
                      tooltip={MENU_CAPTURE_TOOLTIPS.clearDefinition}
                      placement="above"
                      portal
                    >
                      <button
                        type="button"
                        className="capture-de-mot__textarea-clear"
                        onClick={handleClearDefinition}
                        disabled={!formData.definition.trim()}
                        aria-label={MENU_CAPTURE_TOOLTIPS.clearDefinition}
                      >
                        <CircleX size={18} color="#39210d" strokeWidth={1.5} />
                      </button>
                    </MenuIconTooltip>
                  </div>

                  <label
                    className="capture-de-mot__label capture-de-mot__excerpt-label"
                    htmlFor="capture-extrait"
                  >
                    <span className="capture-de-mot__label-title">Extrait</span>
                    <WordFieldCharCount
                      value={formData.extrait}
                      maxLength={WORD_RICH_TEXT_MAX_LENGTH}
                    />
                  </label>
                  <div className="capture-de-mot__textarea-wrap capture-de-mot__textarea-wrap--excerpt">
                    <textarea
                      id="capture-extrait"
                      className="capture-de-mot__textarea"
                      value={formData.extrait}
                      onChange={(e) => handleChange("extrait", e.target.value)}
                    />
                    <MenuIconTooltip
                      className="capture-de-mot__textarea-clear-tip"
                      tooltip={MENU_CAPTURE_TOOLTIPS.clearExcerpt}
                      placement="above"
                      portal
                    >
                      <button
                        type="button"
                        className="capture-de-mot__textarea-clear"
                        onClick={handleClearExtrait}
                        disabled={!formData.extrait.trim()}
                        aria-label={MENU_CAPTURE_TOOLTIPS.clearExcerpt}
                      >
                        <CircleX size={18} color="#39210d" strokeWidth={1.5} />
                      </button>
                    </MenuIconTooltip>
                  </div>

                <div className="capture-de-mot__right-top">
                  <div className="capture-de-mot__toggle-row">
                    <div className="capture-de-mot__toggle-group">
                      <span className="capture-de-mot__toggle-label">Mot favori</span>
                      <div className="capture-de-mot__toggle-slot">
                        <ToggleSwitch
                          checked={isFavorite}
                          onChange={handleFavoriteChange}
                        />
                      </div>
                      <span className="capture-de-mot__toggle-quota">
                        {favoritesCount}
                      </span>
                    </div>

                    <div className="capture-de-mot__toggle-group capture-de-mot__toggle-group--right">
                      <span className="capture-de-mot__toggle-quota">
                        {priorityCount}
                      </span>
                      <div className="capture-de-mot__toggle-slot">
                        <ToggleSwitch
                          checked={isPriority}
                          onChange={handlePriorityChange}
                        />
                      </div>
                      <span className="capture-de-mot__toggle-label">Mot prioritaire</span>
                    </div>
                  </div>

                  <div className="capture-de-mot__groupement-row">
                    <span className="capture-de-mot__toggle-label">
                      Groupement thématique
                    </span>
                    <GroupementAssignDropdown
                      className="capture-de-mot__groupement"
                      groupements={groupements}
                      value={groupementId}
                      onChange={setGroupementId}
                      disabled={!groupements.length}
                    />
                  </div>

                  <div className="capture-de-mot__hidden-words">
                    <label className="capture-de-mot__label">
                      <span className="capture-de-mot__label-title">
                        Mots cachés
                        <VocabInfoTip
                          className="capture-de-mot__hidden-words-info"
                          tooltip={MENU_CAPTURE_TOOLTIPS.hiddenWords}
                          ariaLabel="À propos des mots cachés"
                        />
                      </span>
                    </label>
                    <div className="word-edit-mots-caches capture-de-mot__mots-caches">
                      <div className="capture-de-mot__mots-caches-grid">
                        {motsCaches.map((entry, index) => (
                          <div key={`mot-cache-${index}`} className="word-edit-mots-caches__row">
                            <input
                              type="text"
                              value={entry}
                              onChange={(e) => updateMotCacheRow(index, e.target.value)}
                              className="word-edit-mots-caches__input"
                              placeholder={`Mot caché ${index + 1}`}
                              maxLength={WORD_CACHE_MAX_LENGTH}
                              readOnly={isBaseMotCache(entry, mot)}
                            />
                            <button
                              type="button"
                              className="word-edit-mots-caches__remove"
                              onClick={() => removeMotCacheRow(index)}
                              disabled={
                                motsCaches.length <= 1 || isBaseMotCache(entry, mot)
                              }
                              aria-label={`Supprimer le mot caché ${index + 1}`}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>

                      <div className="word-edit-mots-caches__footer">
                        <button
                          type="button"
                          className="word-edit-mots-caches__add"
                          onClick={addMotCacheRow}
                          disabled={!canAddMotCacheRow}
                          aria-label="Ajouter un mot caché"
                        >
                          +
                        </button>
                        <span className="word-edit-mots-caches__hint">
                          {filledMotsCachesCount}/{WORD_CACHES_MAX}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                  <div className="capture-de-mot__illustration-slot">
                    <div className="word-edit-illustration capture-de-mot__illustration">
                      <div
                        className={`word-edit-illustration__frame${
                          illustrationDataUrl
                            ? " word-edit-illustration__frame--filled"
                            : ""
                        }${
                          !illustrationDataUrl && !illustrationBusy
                            ? " word-edit-illustration__frame--clickable"
                            : ""
                        }`}
                        role={!illustrationDataUrl ? "button" : undefined}
                        tabIndex={
                          !illustrationDataUrl && !illustrationBusy ? 0 : undefined
                        }
                        aria-label={
                          illustrationDataUrl
                            ? "Illustration du mot"
                            : "Ajouter une illustration"
                        }
                        onClick={
                          !illustrationDataUrl
                            ? handleAddIllustrationClick
                            : undefined
                        }
                        onKeyDown={
                          !illustrationDataUrl
                            ? (event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  handleAddIllustrationClick();
                                }
                              }
                            : undefined
                        }
                      >
                        {illustrationDataUrl ? (
                          <img
                            src={illustrationDataUrl}
                            alt={`Illustration de ${mot}`}
                            className="word-edit-illustration__image"
                          />
                        ) : (
                          <span className="word-edit-illustration__placeholder">
                            {illustrationBusy
                              ? "Traitement…"
                              : "Cliquez ici pour ajouter une illustration"}
                          </span>
                        )}
                      </div>

                      {illustrationDataUrl && (
                        <div className="word-edit-illustration__remove-anchor">
                          <MenuIconTooltip
                            tooltip={MENU_CAPTURE_TOOLTIPS.removeIllustration}
                            placement="above"
                            portal
                          >
                            <button
                              type="button"
                              className="word-edit-illustration__remove"
                              onClick={handleClearIllustration}
                              disabled={illustrationBusy}
                              aria-label={MENU_CAPTURE_TOOLTIPS.removeIllustration}
                            >
                              <X size={16} strokeWidth={2.5} />
                            </button>
                          </MenuIconTooltip>
                        </div>
                      )}

                      <input
                        ref={illustrationInputRef}
                        type="file"
                        accept={ILLUSTRATION_ACCEPT}
                        className="word-edit-illustration__file-input"
                        onChange={handleIllustrationSelected}
                      />
                    </div>
                  </div>
              </div>

              <div className="capture-de-mot__actions">
                <button
                  type="submit"
                  className="word-edit-btn word-edit-btn--save capture-de-mot__submit"
                  disabled={!wordConfirmed || !isFormValid || submitting || checking}
                >
                  {submitting ? "Capture en cours…" : "Capturer le mot"}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
